import { useCallback, useMemo } from 'react';

import { useAtom } from 'jotai';

import { useReferralsFilters as useReferralsFiltersAPI } from '../../../react-query/useReferralsList';
import {
  referralsCustomDateRangeAtom,
  referralsIsDatePickerOpenAtom,
  referralsSelectedDateRangeAtom,
  referralsSelectedLocationAtom,
  referralsSelectedSpecialtyAtom,
  referralsSelectedStatusAtom,
} from '../atoms';

// Default filter options
const defaultStatuses = [
  { value: 'all', label: 'All statuses', count: 0 },
  { value: 'submitted', label: 'Submitted', count: 0 },
  { value: 'not_started', label: 'Not started', count: 0 },
  { value: 'needs_referral', label: 'Needs referral', count: 0 },
  { value: 'failed', label: 'Needs Resubmission', count: 0 },
  { value: 'in_progress', label: 'In progress', count: 0 },
  { value: 'completed', label: 'Completed', count: 0 },
];

const defaultDateRanges = [
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'last_3_months', label: 'Last 3 months' },
  { value: 'last_6_months', label: 'Last 6 months' },
  { value: 'this_year', label: 'This year' },
  { value: 'custom', label: 'Custom range' },
];

export const useReferralsFilters = () => {
  // Filter states from atoms
  const [selectedSpecialty, setSelectedSpecialty] = useAtom(
    referralsSelectedSpecialtyAtom,
  );
  const [selectedStatus, setSelectedStatus] = useAtom(
    referralsSelectedStatusAtom,
  );
  const [selectedLocation, setSelectedLocation] = useAtom(
    referralsSelectedLocationAtom,
  );
  const [selectedDateRange, setSelectedDateRange] = useAtom(
    referralsSelectedDateRangeAtom,
  );
  const [customDateRange, setCustomDateRange] = useAtom(
    referralsCustomDateRangeAtom,
  );
  const [isDatePickerOpen, setIsDatePickerOpen] = useAtom(
    referralsIsDatePickerOpenAtom,
  );

  const { data: filtersData } = useReferralsFiltersAPI();

  const specialtyOptions = useMemo(() => {
    if (filtersData?.specialties) {
      return [
        { value: 'all', label: 'All Specialties' },
        ...filtersData.specialties.map((s) => ({
          value: s.id.toString(),
          label: s.name,
        })),
      ];
    }
    return [{ value: 'all', label: 'All Specialties' }];
  }, [filtersData?.specialties]);

  const statusOptions = useMemo(() => {
    if (filtersData?.statuses) {
      return [
        { value: 'all', label: 'All Statuses', count: 0 },
        ...filtersData.statuses.map((s) => ({
          value: s.value,
          label: s.value === 'failed' ? 'Needs Resubmission' : s.label,
          count: s.count,
        })),
      ];
    }
    return defaultStatuses;
  }, [filtersData?.statuses]);

  const locationOptions = useMemo(() => {
    if (filtersData?.referrals_practices) {
      return [
        { value: 'all', label: 'All Specialty Practices', count: 0 },
        ...filtersData.referrals_practices.map((practice) => ({
          value: practice.id,
          label: practice.name,
          count: practice.count,
        })),
      ];
    }
    return [{ value: 'all', label: 'All Specialty Practices', count: 0 }];
  }, [filtersData?.referrals_practices]);

  const dateRangeOptions = useMemo(() => {
    if (filtersData?.dateRanges) {
      return filtersData.dateRanges;
    }
    return defaultDateRanges;
  }, [filtersData?.dateRanges]);

  // Compute display values when needed
  const getSpecialtyDisplayValue = useMemo(() => {
    const option = specialtyOptions.find(
      (opt) => opt.value === selectedSpecialty,
    );
    return option?.label || 'All Specialties';
  }, [specialtyOptions, selectedSpecialty]);

  const getStatusDisplayValue = useMemo(() => {
    const option = statusOptions.find((opt) => opt.value === selectedStatus);
    return option?.label || 'All Statuses';
  }, [statusOptions, selectedStatus]);

  const getLocationDisplayValue = useMemo(() => {
    const option = locationOptions.find(
      (opt) => opt.value === selectedLocation,
    );
    return option?.label || 'All Specialty Practices';
  }, [locationOptions, selectedLocation]);

  const getDateRangeDisplayValue = useMemo(() => {
    const option = dateRangeOptions.find(
      (opt) => opt.value === selectedDateRange,
    );
    return option?.label || 'This month';
  }, [dateRangeOptions, selectedDateRange]);

  const handleSpecialtyChange = useCallback(
    (value: string) => {
      setSelectedSpecialty(value);
    },
    [setSelectedSpecialty],
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      setSelectedStatus(value);
    },
    [setSelectedStatus],
  );

  const handleLocationChange = useCallback(
    (value: string) => {
      setSelectedLocation(value);
    },
    [setSelectedLocation],
  );

  const handleDateRangeChange = useCallback(
    (value: string) => {
      setSelectedDateRange(value);
    },
    [setSelectedDateRange],
  );

  const handleCustomDateRangeChange = useCallback(
    (range: any) => {
      setCustomDateRange(range);
    },
    [setCustomDateRange],
  );

  const handleDatePickerOpenChange = useCallback(
    (open: boolean) => {
      setIsDatePickerOpen(open);
    },
    [setIsDatePickerOpen],
  );

  const resetFilters = useCallback(() => {
    setSelectedSpecialty('all');
    setSelectedStatus('all');
    setSelectedLocation('all');
    setSelectedDateRange('this_month');
    setCustomDateRange(undefined);
  }, [
    setSelectedSpecialty,
    setSelectedStatus,
    setSelectedLocation,
    setSelectedDateRange,
    setCustomDateRange,
  ]);

  return {
    selectedSpecialty,
    selectedStatus,
    selectedLocation,
    selectedDateRange,
    customDateRange,
    isDatePickerOpen,

    specialtyOptions,
    statusOptions,
    locationOptions,
    dateRangeOptions,

    getSpecialtyDisplayValue,
    getStatusDisplayValue,
    getLocationDisplayValue,
    getDateRangeDisplayValue,

    handleSpecialtyChange,
    handleStatusChange,
    handleLocationChange,
    handleDateRangeChange,
    handleCustomDateRangeChange,
    handleDatePickerOpenChange,
    resetFilters,
  };
};
